import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Maximize2,
  Menu,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
} from 'lucide-react';
import {
  normalizeCatalogOptions,
  readWbsItemSystems,
  type WbsCatalogOption,
  type WbsItemDoc,
} from '@mct/shared';
import { useAssignees, useProject } from '../hooks/useFirestoreData';
import { useWbsConstraints, useWbsItems } from '../hooks/useWbs';
import {
  createAssignee,
  deleteAssignee,
  renameAssignee,
  updateAssigneeColorIndex,
} from '../lib/assigneeOps';
import { Spinner } from '../components/ui/Spinner';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Input, Label } from '../components/ui/Input';
import { WbsGantt } from '../components/Wbs/WbsGantt';
import { WbsTreeTable, type FlatNode } from '../components/Wbs/WbsTreeTable';
import { WbsFilterBar } from '../components/Wbs/WbsFilterBar';
import { WbsAreaFilter } from '../components/Wbs/WbsAreaFilter';
import { AREA_LABELS, buildLabelColorIndexMap } from '../lib/areaColor';
import {
  CatalogOptionsEditor,
  type CatalogOptionEditResult,
} from '../components/Wbs/CatalogOptionsEditor';
import { WbsItemDetailPanel } from '../components/Wbs/WbsItemDetailPanel';
import {
  buildTree,
  collapsePhaseInnerParents,
  flattenTree,
  type WbsTreeNode,
} from '../lib/wbsTree';
import {
  ZOOM_DAY_WIDTH,
  chartPixelWidth,
  computeRange,
  tsToDate,
  type GanttZoom,
} from '../lib/wbsGantt';
import {
  EMPTY_WBS_FILTER,
  collectFacets,
  countAppliedFilters,
  decodeWbsFilter,
  encodeWbsFilter,
  filterItems,
} from '../lib/wbsFilter';
import {
  applyWbsMoveWrites,
  appendWbsItem,
  renameWbsItemOption,
  updateWbsItem,
  type UpdateWbsItemPatch,
} from '../lib/wbs';
import { updateWbsCatalog } from '../lib/firestore';
import {
  buildDescendantIndex,
  buildMoveWrites,
  describeMoveError,
  resolveMove,
  type DropPosition,
} from '../lib/wbsResort';
import { getDb } from '../lib/firebase';
import { cn } from '../lib/utils';
import { useViewportTier } from '../lib/useViewportTier';
import { useDismissable } from '../lib/useDismissable';
import { useWbsColumnWidths } from '../lib/wbsColumnWidths';

const FILTER_OPEN_KEY = 'wbs-filter-open';

export function WbsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;
  return <WbsContent projectId={projectId} />;
}

function WbsContent({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const { data: items, loading } = useWbsItems(projectId);
  const { data: constraints } = useWbsConstraints(projectId);
  // Single source of truth for the assignee picker — same hook the
  // migration-task DAG view and the Settings page consume, so a chip
  // added to the Settings list is immediately available here.
  const { data: assignees } = useAssignees(projectId);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(() => decodeWbsFilter(searchParams), [searchParams]);
  const appliedCount = countAppliedFilters(filter);
  const hasActiveFilter = appliedCount > 0;

  const updateFilter = (next: typeof filter) => {
    setSearchParams(encodeWbsFilter(next, searchParams), { replace: true });
  };

  const [zoom, setZoom] = useState<GanttZoom>('week');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Deep-link target — TaskListPage の完了基準バナーから
  // `?focus=<itemId>` を付けて遷移されてくる導線をフックする。
  // selectedId にコピーしたら URL 側はクリーンアップしてリロード時に
  // モーダルが復活しないようにする。
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (!focusId) return;
    setSelectedId(focusId);
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Phase 1 responsive: emit a tier-appropriate gridTemplate so the WBS
  // tree drops 領域 / ベンダー / 担当 / 予定開始 / 予定終了 columns at
  // narrow viewports. The tree cells carry matching `hidden / sm:flex /
  // lg:flex` Tailwind classes so the visible cells flow into the tier's
  // grid columns 1:1 in DOM order.
  const tier = useViewportTier();
  const isMobile = tier === 'mobile';
  const isDesktop = tier === 'desktop';

  // Owns the WBS tree's column widths (persisted to localStorage inside
  // the hook) so the sticky tree column's outer width stays in sync
  // with the inner grid — otherwise resizing 名称 wider would make the
  // tree spill onto the Gantt area.
  const treeColumns = useWbsColumnWidths(tier);
  const treeWidth = treeColumns.totalWidth;
  const [reorderToast, setReorderToast] = useState<
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string }
    | null
  >(null);
  const [creating, setCreating] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // Tri-state view mode: 'both' shows tree + gantt side-by-side
  // (default), 'list-only' hides the gantt area, 'gantt-only' hides the
  // tree. リストのみ / ガントのみ are mutually exclusive — clicking one
  // when the other is active replaces it.
  const [viewMode, setViewMode] = useState<'both' | 'list-only' | 'gantt-only'>(
    'both',
  );
  const ganttOnly = viewMode === 'gantt-only';
  const listOnly = viewMode === 'list-only';
  const setGanttOnly = (next: boolean | ((v: boolean) => boolean)) => {
    const v = typeof next === 'function' ? next(ganttOnly) : next;
    setViewMode(v ? 'gantt-only' : 'both');
  };
  const setListOnly = (next: boolean | ((v: boolean) => boolean)) => {
    const v = typeof next === 'function' ? next(listOnly) : next;
    setViewMode(v ? 'list-only' : 'both');
  };
  // In listOnly mode the gantt pane is gone, so the tree fills whatever
  // horizontal space is left after the detail panel. The base
  // `gridTemplate` uses all-px columns (sum = treeWidth) which can't
  // adapt. Swap the name column for a `minmax(<userWidth>px, 1fr)`
  // track so:
  //   - container ≥ treeWidth → name absorbs the surplus (画面いっぱい)
  //   - container < treeWidth → name shrinks (other columns stay
  //     readable; the name text truncates via existing line-clamp).
  // Only swap when listOnly is active so the side-by-side gantt+tree
  // layout keeps its byte-for-byte identity.
  const effectiveGridTemplate = useMemo(() => {
    if (!listOnly) return treeColumns.gridTemplate;
    const parts = treeColumns.gridTemplate.split(' ');
    if (parts.length >= 2) {
      parts[1] = `minmax(${parts[1]}, 1fr)`;
    }
    return parts.join(' ');
  }, [listOnly, treeColumns.gridTemplate]);
  const [filterOpen, setFilterOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(FILTER_OPEN_KEY) === '1';
  });
  const [openEditor, setOpenEditor] = useState<
    'system' | 'assignee' | null
  >(null);
  /**
   * Mobile burger menu visibility. Below `sm` we collapse the toolbar
   * actions (フィルタ / 全展開 / 全折りたたみ / 行追加 / ガントのみ /
   * 全画面) into a single ⋯ menu so the header doesn't wrap.
   */
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(FILTER_OPEN_KEY, filterOpen ? '1' : '0');
  }, [filterOpen]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Mobile detail-overlay: Esc closes the panel just like the ✕ button.
  // Desktop side-panel doesn't take focus so Esc would be a confusing
  // shortcut there — gate this on tier.
  useEffect(() => {
    if (!isMobile) return;
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, selectedId]);

  // Snap the burger menu shut when the viewport widens past the mobile
  // breakpoint — leaving it `open` past sm would render a stale dropdown
  // card behind the fully-expanded toolbar.
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) setMobileMenuOpen(false);
  }, [isMobile, mobileMenuOpen]);

  // Phase 2.3: outside-click + Esc + focus-trap UX for the burger menu.
  // Centralised in `useDismissable` so any future popover gets the same
  // contract by importing the hook (instead of re-implementing it).
  const burgerTriggerRef = useRef<HTMLButtonElement | null>(null);
  const burgerPanelRef = useRef<HTMLDivElement | null>(null);
  useDismissable({
    open: mobileMenuOpen,
    onClose: () => setMobileMenuOpen(false),
    containerRef: burgerPanelRef,
    triggerRef: burgerTriggerRef,
  });

  const now = useMemo(() => new Date(), []);

  const tree = useMemo(() => buildTree(items), [items]);

  // Seed the expanded set once when the tree first loads. Using a ref
  // instead of `expanded.size` as a trigger prevents the effect from
  // re-expanding roots right after the user clicks 全折りたたみ.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (tree.length === 0) return;
    const initial = new Set<string>();
    for (const root of tree) {
      initial.add(root.item.itemId);
      for (const c of root.children) initial.add(c.item.itemId);
    }
    setExpanded(initial);
    seededRef.current = true;
  }, [tree]);

  const { visibleIds, directMatchIds } = useMemo(
    () => filterItems(items, filter, now),
    [items, filter, now],
  );

  const flatNodes: FlatNode[] = useMemo(() => {
    const flat = flattenTree(tree, expanded);
    const keep = hasActiveFilter ? visibleIds : null;
    const out: FlatNode[] = [];
    for (const node of flat) {
      if (keep && !keep.has(node.item.itemId)) continue;
      const hiddenChildren = node.children.some(
        (c) => !keep || keep.has(c.item.itemId),
      )
        ? !expanded.has(node.item.itemId)
        : false;
      out.push({
        node,
        hasHiddenChildren: hiddenChildren && node.children.length > 0,
        directMatch: hasActiveFilter ? directMatchIds.has(node.item.itemId) : true,
      });
    }
    return out;
  }, [tree, expanded, visibleIds, directMatchIds, hasActiveFilter]);

  const visibleItems = useMemo(
    () => flatNodes.map((f) => f.node.item),
    [flatNodes],
  );

  const range = useMemo(() => {
    const dates: (Date | null)[] = [];
    for (const item of visibleItems) {
      dates.push(tsToDate(item.plannedStart));
      dates.push(tsToDate(item.plannedEnd));
      dates.push(tsToDate(item.actualStart));
      dates.push(tsToDate(item.actualEnd));
    }
    if (constraints) {
      for (const h of constraints.holidays) {
        dates.push(tsToDate(h.start));
        dates.push(tsToDate(h.end));
      }
    }
    dates.push(now);
    return (
      computeRange(dates, 14) ?? {
        start: new Date(now.getTime() - 30 * 86400000),
        end: new Date(now.getTime() + 60 * 86400000),
      }
    );
  }, [visibleItems, constraints, now]);

  const totalGanttWidth = useMemo(
    () => chartPixelWidth(range.start, range.end, zoom),
    [range, zoom],
  );

  const facets = useMemo(() => collectFacets(items), [items]);

  // Effective catalog = stored project.wbsCatalog overridden by whatever is
  // actually present in the loaded items so legacy projects (that never
  // wrote a catalog) still get a useful option list. Known area labels
  // are always included so the fixed 5 buckets stay visible.
  //
  // Output is the structured `WbsCatalogOption[]` shape —
  // `normalizeCatalogOptions` wraps any legacy `string[]` entries so
  // downstream consumers don't have to branch on the storage form.
  // Auto-derived rows (legacy free-text values that exist on items but
  // weren't in the stored catalog) come through with
  // `colorIndex: undefined` so they fall back to the deterministic name
  // hash until the user opens the editor.
  const areaCatalog = useMemo<WbsCatalogOption[]>(() => {
    const normalized = normalizeCatalogOptions(project?.wbsCatalog?.area);
    const byLabel = new Map<string, WbsCatalogOption>();
    for (const opt of normalized) byLabel.set(opt.label, opt);
    for (const label of AREA_LABELS) {
      if (!byLabel.has(label)) byLabel.set(label, { id: label, label });
    }
    for (const v of facets.systems) {
      if (!byLabel.has(v)) byLabel.set(v, { id: v, label: v });
    }
    return Array.from(byLabel.values());
  }, [project?.wbsCatalog?.area, facets.systems]);

  /** Convenience: just the labels, in catalog order, for places that don't care about color. */
  const areaLabels = useMemo<string[]>(
    () => areaCatalog.map((o) => o.label),
    [areaCatalog],
  );

  /** `label -> colorIndex` for the WBS tree's area badge resolution. */
  const areaColorIndices = useMemo(
    () => buildLabelColorIndexMap(areaCatalog),
    [areaCatalog],
  );

  /**
   * `area-label -> count` snapshot for the delete-guard. Computed once
   * (cheap O(items) scan; rebuilt on each `items` change). 領域 is
   * multi-value, so a row contributes to each of its labels.
   */
  const areaUsageCounts = useMemo<Map<string, number>>(() => {
    const counts = new Map<string, number>();
    for (const it of items) {
      for (const raw of readWbsItemSystems(it)) {
        const v = raw.trim();
        if (!v) continue;
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
    }
    return counts;
  }, [items]);

  // Vendor catalog: same dual-shape adapter as area. Picks up any free-
  // text vendor values that existed on items before the structured
  // catalog was written.
  const vendorCatalog = useMemo<WbsCatalogOption[]>(() => {
    const normalized = normalizeCatalogOptions(project?.wbsCatalog?.vendor);
    const byLabel = new Map<string, WbsCatalogOption>();
    for (const opt of normalized) byLabel.set(opt.label, opt);
    for (const v of facets.vendors) {
      if (!byLabel.has(v)) byLabel.set(v, { id: v, label: v });
    }
    return Array.from(byLabel.values());
  }, [project?.wbsCatalog?.vendor, facets.vendors]);

  const vendorLabels = useMemo<string[]>(
    () => vendorCatalog.map((o) => o.label),
    [vendorCatalog],
  );

  const vendorColorIndices = useMemo(
    () => buildLabelColorIndexMap(vendorCatalog),
    [vendorCatalog],
  );

  // The assignee dropdown source for both the WBS row picker and the
  // detail panel is `useAssignees` — the canonical list under
  // `/projects/{pid}/assignees`. Keeping a reference here so the rest of
  // the page reads naturally.
  const assigneeCatalog = assignees;

  /**
   * Catalog form for the 担当 editor: id is the assigneeId (so the
   * editor's `renames` map keys back to the right doc on save), label
   * is the display name, colorIndex is the frozen palette slot.
   */
  const assigneeCatalogOptions = useMemo<WbsCatalogOption[]>(
    () =>
      assignees.map((a) => ({
        id: a.assigneeId,
        label: a.name,
        colorIndex: a.colorIndex,
      })),
    [assignees],
  );

  /**
   * `assigneeId -> WBS-item count` for the editor's delete-guard. After
   * the canonical migration `assigneeId` is the only link from items to
   * assignees, so we count by id and key the map by id (the editor maps
   * id -> usage internally via its `options`).
   */
  const assigneeUsageCounts = useMemo<Map<string, number>>(() => {
    const counts = new Map<string, number>();
    for (const it of items) {
      const id = it.assigneeId ?? null;
      if (!id) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const onEditItem = async (itemId: string, patch: UpdateWbsItemPatch) => {
    await updateWbsItem(getDb(), projectId, itemId, patch);
  };

  /**
   * Generic save handler for the 領域 / ベンダー catalog editors.
   * Multi-layered delete guard:
   *   1. UI disables the trash button on rows whose label has usage
   *      count > 0 (snapshot taken at dialog open).
   *   2. `CatalogOptionsEditor.save()` re-checks against the same
   *      snapshot before emitting deletes.
   *   3. Here, we re-compute usage from the live `items` array (which
   *      may have advanced between dialog open and save thanks to
   *      Firestore's real-time listener) and refuse deletes whose
   *      target label is now in use, before any write fires.
   *
   * `field` selects which `wbsItems[*]` field carries the catalog value
   * (`system` for 領域, `vendor` for ベンダー); the save also patches
   * the matching half of `WbsCatalog` while preserving the other half.
   */
  const saveCatalogOptions = async (
    field: 'system' | 'vendor',
    result: CatalogOptionEditResult,
    snapshot: readonly WbsCatalogOption[],
  ) => {
    const originalsById = new Map(snapshot.map((o) => [o.id, o.label]));
    // Defence-in-depth: the editor already pre-checks against the
    // dialog-open usage snapshot, but a concurrent writer can move an
    // item between open and save. Re-scan live `items` and refuse if
    // any delete target is now in use.
    for (const [id, target] of result.renames) {
      if (target === null) {
        const origLabel = originalsById.get(id);
        if (origLabel) {
          const liveCount = items.filter((it) =>
            field === 'system'
              ? readWbsItemSystems(it).includes(origLabel)
              : ((it as unknown as Record<string, unknown>).vendor ?? '') ===
                origLabel,
          ).length;
          if (liveCount > 0) {
            throw new Error(
              `「${origLabel}」は ${liveCount} 件のタスクで使用中のため削除できません`,
            );
          }
        }
      }
    }
    // 1. Apply renames / deletes to any wbsItems that use the old label.
    //    The migration helper takes the *original* label, not the id.
    for (const [id, to] of result.renames) {
      const origLabel = originalsById.get(id);
      if (!origLabel) continue;
      await renameWbsItemOption(getDb(), projectId, field, origLabel, to, items);
    }
    // 2. Persist the new option list (with colorIndex) on the project doc.
    //    Preserve the other half of the catalog as-is.
    const nextArea =
      field === 'system' ? result.options : (project?.wbsCatalog?.area ?? areaCatalog);
    const nextVendor =
      field === 'vendor' ? result.options : (project?.wbsCatalog?.vendor ?? vendorCatalog);
    await updateWbsCatalog(getDb(), projectId, {
      area: nextArea,
      vendor: nextVendor,
    });
    // 3. Filter sync (area only): if the user renamed an area that's
    //    currently filtered on, update the URL-backed filter so the
    //    selection tracks the rename. Vendor isn't in the filter chip
    //    row so no equivalent step is needed.
    if (field === 'system') {
      const labelByOriginalLabel = new Map<string, string | null>();
      for (const [id, target] of result.renames) {
        const origLabel = originalsById.get(id);
        if (origLabel) labelByOriginalLabel.set(origLabel, target);
      }
      if (labelByOriginalLabel.size > 0) {
        const nextSystems: string[] = [];
        let changed = false;
        for (const s of filter.systems) {
          if (labelByOriginalLabel.has(s)) {
            const to = labelByOriginalLabel.get(s);
            if (to) nextSystems.push(to);
            changed = true;
          } else {
            nextSystems.push(s);
          }
        }
        if (changed) updateFilter({ ...filter, systems: nextSystems });
      }
    }
  };

  /**
   * Save handler for the 担当 editor. Unlike 領域 / ベンダー, assignees
   * live in their own subcollection (`/projects/{pid}/assignees`), so
   * we diff the editor's `result` against the original `assigneeDocs`
   * and dispatch one `createAssignee` / `renameAssignee` /
   * `updateAssigneeColorIndex` / `deleteAssignee` per change.
   *
   * Defence-in-depth: the editor disables the trash for in-use rows
   * via `assigneeUsageCounts` taken at dialog open. Here we re-scan
   * live `items` and refuse the delete if a parallel writer attached
   * an item to the assignee between open and save.
   */
  const saveAssigneeCatalog = async (result: CatalogOptionEditResult) => {
    const originalById = new Map(
      assignees.map((a) => [a.assigneeId, a] as const),
    );
    // Pre-flight: refuse deletes whose target is in use right now.
    for (const [id, target] of result.renames) {
      if (target === null) {
        const orig = originalById.get(id);
        if (orig) {
          const liveCount = items.filter((it) => it.assigneeId === id).length;
          if (liveCount > 0) {
            throw new Error(
              `「${orig.name}」は ${liveCount} 件のタスクで使用中のため削除できません`,
            );
          }
        }
      }
    }
    // 1. Apply rename / delete diffs.
    for (const [id, target] of result.renames) {
      const orig = originalById.get(id);
      if (!orig) continue;
      if (target === null) {
        await deleteAssignee(getDb(), projectId, id);
      } else if (target !== orig.name) {
        await renameAssignee(getDb(), projectId, id, target);
      }
    }
    // 2. Apply color-index changes for kept rows. The editor doesn't
    //    diff colors itself, so we walk every option and write the
    //    colorIndex if it differs from the doc's current value.
    for (const opt of result.options) {
      const orig = originalById.get(opt.id);
      if (!orig) continue; // new rows handled in step 3
      if (
        typeof opt.colorIndex === 'number' &&
        opt.colorIndex !== orig.colorIndex
      ) {
        await updateAssigneeColorIndex(getDb(), projectId, opt.id, opt.colorIndex);
      }
    }
    // 3. Newly-added rows: any option whose `id` matches its `label`
    //    (the editor's "this row didn't exist before" sentinel — the
    //    save derivation graduates `__new_*` to the label-derived id).
    for (const opt of result.options) {
      if (originalById.has(opt.id)) continue;
      await createAssignee(getDb(), project!, {
        name: opt.label,
        colorIndex: opt.colorIndex,
      });
    }
  };

  const selectedItem = useMemo(
    () => items.find((i) => i.itemId === selectedId) ?? null,
    [items, selectedId],
  );

  const descendantIndex = useMemo(
    () => buildDescendantIndex(items),
    [items],
  );

  const isDropValid = useMemo(
    () => (draggedId: string, targetId: string, position: DropPosition) => {
      const res = resolveMove(
        items,
        { draggedId, targetId, position },
        descendantIndex,
      );
      return !('kind' in res);
    },
    [items, descendantIndex],
  );

  const onReorder = async (intent: {
    draggedId: string;
    targetId: string;
    position: DropPosition;
  }) => {
    const res = resolveMove(items, intent, descendantIndex);
    if ('kind' in res) {
      setReorderToast({ kind: 'error', message: describeMoveError(res) });
      window.setTimeout(() => setReorderToast(null), 4000);
      return;
    }
    const writes = buildMoveWrites(items, res);
    if (writes.updates.size === 0) return;
    try {
      await applyWbsMoveWrites(getDb(), projectId, writes.updates);
    } catch (err) {
      console.error('WBS reorder failed', err);
      setReorderToast({ kind: 'error', message: '並び替えの保存に失敗しました' });
      window.setTimeout(() => setReorderToast(null), 4000);
    }
  };

  const descendantIdsBy = useMemo(() => {
    const map = new Map<string, string[]>();
    const walk = (node: WbsTreeNode): string[] => {
      const out: string[] = [];
      for (const c of node.children) {
        out.push(c.item.itemId);
        out.push(...walk(c));
      }
      map.set(node.item.itemId, out);
      return out;
    };
    for (const root of tree) walk(root);
    return map;
  }, [tree]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!selectedId || !scrollRef.current) return;
    const item = items.find((i) => i.itemId === selectedId);
    if (!item) return;
    const start = tsToDate(item.plannedStart);
    if (!start) return;
    const dayWidth = ZOOM_DAY_WIDTH[zoom];
    const delta = Math.floor(
      (start.getTime() - range.start.getTime()) / 86400000,
    );
    const x = delta * dayWidth;
    scrollRef.current.scrollLeft = Math.max(0, x - 200);
  }, [selectedId, items, range.start, zoom]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpanded(new Set(items.map((i) => i.itemId)));
  };
  const collapseAll = () => setExpanded(new Set());
  // "フェーズ内親タスクのみ折りたたむ": keep phases open but collapse the
  // parent tasks directly beneath them. Pure derivation lives in wbsTree.
  const collapsePhaseParents = () =>
    setExpanded((prev) => collapsePhaseInnerParents(tree, prev));

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!project) return null;

  const body = (
    // Root is a vertical stack so the toolbar / area chips / filter bar
    // stay full-width regardless of whether the detail panel is open.
    // Only the inner main-row (tree+gantt + DetailPanel) splits horizontally.
    <div
      data-testid="wbs-page-root"
      className={cn(
        'relative flex h-full flex-col',
        fullscreen ? 'bg-white' : '',
      )}
    >
      <div
        data-testid="wbs-toolbar"
        className="relative flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2"
      >
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="truncate text-sm font-semibold">WBS / ガント</h2>
          <span className="hidden text-xs text-slate-500 sm:inline">
            {items.length} 件 / 表示 {visibleItems.length}
          </span>
        </div>
        {/*
         * Below `sm` we collapse the toolbar into a single ⋯ button. The
         * full action row reappears at `sm:flex`. Hidden classes ensure
         * the burger and the action row are mutually exclusive at every
         * breakpoint.
         */}
        <button
          ref={burgerTriggerRef}
          type="button"
          data-testid="wbs-toolbar-burger"
          onClick={() => setMobileMenuOpen((v) => !v)}
          // Mobile-tap-target floor: a 32px button under the WCAG / HIG
          // 44px guideline is hard to hit on a phone. `max-lg:` keeps the
          // tighter desktop look while the floor only kicks in below the
          // desktop breakpoint where this button is actually visible.
          className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 max-lg:h-11 max-lg:w-11 sm:hidden"
          aria-label="ツールバーメニュー"
          aria-expanded={mobileMenuOpen}
          aria-haspopup="menu"
        >
          <Menu className="h-4 w-4" />
        </button>
        <div
          data-testid="wbs-toolbar-actions"
          className="hidden items-center gap-2 sm:flex"
        >
          {/*
           * The filter toggle and the expand / collapse-all buttons used
           * to live here. The filter toggle moved into the always-visible
           * WbsAreaFilter row (its leftmost control); expand / collapse
           * moved into the 名称 column header's ListTree dropdown,
           * alongside the new collapse-phase-parents action.
           */}
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> 行追加
          </Button>
          <Button
            variant="secondary"
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? '全画面を解除' : '全画面表示'}
            title={
              fullscreen ? '全画面を解除 (Esc)' : '全画面表示'
            }
          >
            {fullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
        {mobileMenuOpen ? (
          <div
            ref={burgerPanelRef}
            data-testid="wbs-toolbar-mobile-menu"
            className="absolute right-2 top-full z-30 mt-1 flex w-56 flex-col gap-1 rounded-md border border-slate-200 bg-white p-2 shadow-lg sm:hidden"
            role="menu"
            tabIndex={-1}
          >
            {/*
             * The filter toggle and the expand / collapse-all rows were
             * removed from the burger menu: the filter toggle now lives in
             * the always-visible WbsAreaFilter row (reachable on mobile via
             * its leftmost button), and the expand / collapse actions moved
             * into the 名称 column header's ListTree dropdown, which is
             * visible at every breakpoint.
             */}
            <MobileMenuItem
              onClick={() => {
                setCreating(true);
                setMobileMenuOpen(false);
              }}
              icon={<Plus className="h-3.5 w-3.5" />}
              label="行追加"
            />
            {/* Phase 2.3: the gantt-only row dropped from the mobile
                 menu — the gantt area itself is hidden below `lg`, so
                 the toggle has nothing to act on at this width. The
                 desktop toolbar button (right-slot of WbsAreaFilter) is
                 the only entry point now, gated behind `hidden
                 lg:inline-flex` so it never shows up on phones /
                 tablets. */}
          </div>
        ) : null}
      </div>

      <WbsAreaFilter
        // Show the full effective catalog so the 5 standard buckets
        // stay visible and any user-added領域 also get a chip.
        areas={areaLabels}
        colorIndices={areaColorIndices}
        // フィルタ toggle lives here now (leftmost control) instead of the
        // page toolbar — it opens / closes the collapsible WbsFilterBar.
        filterOpen={filterOpen}
        onToggleFilter={() => setFilterOpen((v) => !v)}
        hasActiveFilter={hasActiveFilter}
        selected={filter.systems}
        onToggle={(v) =>
          updateFilter({
            ...filter,
            systems: filter.systems.includes(v)
              ? filter.systems.filter((s) => s !== v)
              : [...filter.systems, v],
          })
        }
        hideDone={filter.hideDone}
        onToggleHideDone={() =>
          updateFilter({ ...filter, hideDone: !filter.hideDone })
        }
        delayed={filter.delayed}
        onToggleDelayed={() =>
          updateFilter({ ...filter, delayed: !filter.delayed })
        }
        search={filter.search}
        onSearchChange={(value) => updateFilter({ ...filter, search: value })}
        rightSlot={
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded border border-slate-200 text-xs">
              {(['day', 'week', 'month'] as const).map((z) => (
                <button
                  key={z}
                  type="button"
                  className={
                    'px-2 py-0.5 ' +
                    (zoom === z
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-100')
                  }
                  onClick={() => setZoom(z)}
                >
                  {z === 'day' ? '日' : z === 'week' ? '週' : '月'}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="wbs-list-only-toggle"
              onClick={() => setListOnly((v) => !v)}
              // Same `lg+`-only gate as ガントのみ — the Gantt pane only
              // renders at lg, so on smaller widths the tree is already
              // alone and this toggle would be a no-op.
              className={cn(
                'hidden items-center gap-1 rounded border px-2 py-0.5 text-xs transition-colors lg:inline-flex',
                listOnly
                  ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
              )}
              aria-pressed={listOnly}
              title={listOnly ? 'ガントも表示' : 'リストのみ表示'}
            >
              {listOnly ? (
                <PanelLeftClose className="h-3.5 w-3.5" />
              ) : (
                <PanelLeftOpen className="h-3.5 w-3.5" />
              )}
              <span>{listOnly ? 'ガントも表示' : 'リストのみ'}</span>
            </button>
            <button
              type="button"
              data-testid="wbs-gantt-only-toggle"
              onClick={() => setGanttOnly((v) => !v)}
              // Phase 2.3: hidden below `lg` because the Gantt area
              // itself is `hidden lg:block` (mobile / tablet renders the
              // tree only). A toggle that flips an invisible pane just
              // confuses tap targets, so the whole control is gated.
              className={cn(
                'hidden items-center gap-1 rounded border px-2 py-0.5 text-xs transition-colors lg:inline-flex',
                ganttOnly
                  ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
              )}
              aria-pressed={ganttOnly}
              title={ganttOnly ? 'WBS表を表示' : 'ガントのみ表示'}
            >
              {ganttOnly ? (
                <PanelLeftOpen className="h-3.5 w-3.5" />
              ) : (
                <PanelLeftClose className="h-3.5 w-3.5" />
              )}
              <span>{ganttOnly ? 'WBS表を表示' : 'ガントのみ'}</span>
            </button>
          </div>
        }
      />

      {filterOpen ? (
        <div id="wbs-filter-panel">
          <WbsFilterBar
            filter={filter}
            assignees={assigneeCatalog}
            onChange={updateFilter}
            onClear={() => updateFilter(EMPTY_WBS_FILTER)}
            hasActive={hasActiveFilter}
          />
        </div>
      ) : null}

      {reorderToast ? (
        <div
          className={cn(
            'border-b px-4 py-1.5 text-xs',
            reorderToast.kind === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800',
          )}
        >
          {reorderToast.message}
        </div>
      ) : null}

      {/*
       * Main row: tree+gantt on the left (flex-1), detail panel on the
       * right (fixed width). The header / toolbar / area-chip / filter
       * rows live above this row, so opening the detail panel only
       * shrinks the tree+gantt area — the toolbar's width stays put.
       */}
      <div
        data-testid="wbs-main-row"
        className="flex min-h-0 flex-1 overflow-hidden"
      >
        <div
          ref={scrollRef}
          className="relative min-w-0 flex-1 overflow-auto"
        >
          {flatNodes.length === 0 ? (
            <EmptyState
              message={
                items.length === 0
                  ? 'WBS 行はまだありません。右上の「行追加」から作成してください。'
                  : '条件に一致する行がありません'
              }
            />
          ) : (
            <div
              className={cn(
                'flex',
                // Mobile / tablet: the tree owns the entire viewport
                // width. Desktop listOnly: same — the tree fills 100%
                // of the (scroll-container-width) and the inner grid's
                // name column is the flex track that soaks up surplus
                // or shrinks under pressure. No min-width clamp here
                // so the detail panel can open without forcing a
                // horizontal scroll.
                isDesktop && !listOnly ? '' : 'w-full',
              )}
              style={
                isDesktop && !listOnly
                  ? {
                      width:
                        (ganttOnly ? 0 : treeWidth) +
                        (listOnly ? 0 : totalGanttWidth),
                    }
                  : undefined
              }
            >
              {ganttOnly && isDesktop ? null : (
                <div
                  className={cn(
                    'shrink-0 border-r border-slate-200 bg-white',
                    isDesktop && !listOnly
                      ? 'sticky left-0 z-30'
                      : 'min-w-0 flex-1',
                  )}
                  style={
                    isDesktop && !listOnly
                      ? { width: treeWidth }
                      : undefined
                  }
                >
                  <WbsTreeTable
                    nodes={flatNodes}
                    expanded={expanded}
                    selectedId={selectedId}
                    hoveredId={hoveredId}
                    now={now}
                    onToggle={toggle}
                    onSelect={setSelectedId}
                    onHover={setHoveredId}
                    onReorder={onReorder}
                    isDropValid={isDropValid}
                    gridTemplate={effectiveGridTemplate}
                    beginResize={treeColumns.beginResize}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                    onCollapsePhaseParents={collapsePhaseParents}
                    openEditor={openEditor}
                    onOpenEditor={setOpenEditor}
                    areaOptions={areaLabels}
                    areaColorIndices={areaColorIndices}
                    assigneeOptions={assigneeCatalog}
                    onEdit={onEditItem}
                    backlogSpaceUrl={project.backlog?.spaceUrl ?? null}
                    scrollContainerRef={scrollRef}
                    editorSlot={
                      openEditor === 'system' ? (
                        <CatalogOptionsEditor
                          title="領域の選択肢を編集"
                          noun="領域"
                          options={areaCatalog}
                          usageCounts={areaUsageCounts}
                          locked={AREA_LABELS}
                          onClose={() => setOpenEditor(null)}
                          onSave={(r) => saveCatalogOptions('system', r, areaCatalog)}
                        />
                      ) : openEditor === 'assignee' ? (
                        <CatalogOptionsEditor
                          title="担当者リストを編集"
                          noun="担当"
                          options={assigneeCatalogOptions}
                          usageCounts={assigneeUsageCounts}
                          onClose={() => setOpenEditor(null)}
                          onSave={saveAssigneeCatalog}
                        />
                      ) : null
                    }
                  />
                </div>
              )}
              {/*
               * Phase 1 responsive: the Gantt area is hidden below `lg`.
               * On a phone / tablet the bars are illegibly narrow and
               * the horizontal scroll competes with vertical scroll for
               * the same touch axis, so the planning view is desktop-
               * only for now (Asana-style consume-on-mobile split).
               */}
              <div
                data-testid="wbs-gantt-area"
                className={cn(
                  'shrink-0',
                  // Always hidden < lg; on lg+ hide when "リストのみ".
                  listOnly ? 'hidden' : 'hidden lg:block',
                )}
                style={{ width: listOnly ? 0 : totalGanttWidth }}
              >
                <WbsGantt
                  items={items}
                  visibleItems={visibleItems}
                  constraints={constraints}
                  range={range}
                  zoom={zoom}
                  selectedId={selectedId}
                  hoveredId={hoveredId}
                  now={now}
                  onSelect={setSelectedId}
                  onHover={setHoveredId}
                />
              </div>
            </div>
          )}
        </div>

        {selectedItem ? (
          /*
           * Detail panel responsive shell.
           *
           * - Below `lg`: full-viewport fixed overlay (panel + dim
           *   layer) so the user can read / edit a row without
           *   competing with the tree for narrow mobile widths.
           * - `lg+`: right-edge **overlay** (not in-flow) so the tree
           *   columns stay at their natural widths instead of shrinking
           *   when the panel opens — opening the panel never reflows
           *   the list. Mirrors the TaskListPage overlay pattern.
           */
          <div
            data-testid="wbs-detail-panel-shell"
            className={cn(
              // lg+: absolute overlay on the right edge of the panel
              // container, lifted above the tree with z-30 + shadow.
              'lg:absolute lg:inset-y-0 lg:right-0 lg:z-30 lg:flex lg:max-w-full lg:shadow-xl',
              // mobile / tablet: full-viewport fixed overlay (also lifts
              // the panel above the tree but covers the whole screen).
              'fixed inset-0 z-40 flex h-screen w-screen',
            )}
          >
            <button
              type="button"
              aria-label="詳細パネルを閉じる"
              onClick={() => setSelectedId(null)}
              className="absolute inset-0 bg-slate-900/30 lg:hidden"
            />
            <WbsItemDetailPanel
              project={project}
              item={selectedItem}
              allItems={items}
              assignees={assigneeCatalog}
              areaOptions={areaLabels}
              areaColorIndices={areaColorIndices}
              vendorOptions={vendorLabels}
              vendorColorIndices={vendorColorIndices}
              hasChildren={(descendantIdsBy.get(selectedItem.itemId) ?? []).length > 0}
              descendantIds={descendantIdsBy.get(selectedItem.itemId) ?? []}
              onClose={() => setSelectedId(null)}
              onSelectItem={setSelectedId}
            />
          </div>
        ) : null}
      </div>

      <CreateRowDialog
        open={creating}
        onClose={() => setCreating(false)}
        items={items}
        onCreate={async (input) => {
          if (!project) return;
          await appendWbsItem(getDb(), project, items, input);
          setCreating(false);
        }}
      />
    </div>
  );

  if (fullscreen && typeof document !== 'undefined') {
    return createPortal(
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        {body}
      </div>,
      document.body,
    );
  }
  return body;
}

function CreateRowDialog({
  open,
  onClose,
  items,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  items: readonly WbsItemDoc[];
  onCreate: (input: {
    kind: 'phase' | 'task' | 'milestone';
    name: string;
    parentItemId: string | null;
  }) => Promise<void>;
}) {
  const [kind, setKind] = useState<'phase' | 'task' | 'milestone'>('task');
  const [name, setName] = useState('');
  const [parentItemId, setParentItemId] = useState<string>('');
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="WBS 行を追加"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            disabled={busy || !name.trim()}
            onClick={async () => {
              setBusy(true);
              try {
                await onCreate({
                  kind,
                  name,
                  parentItemId: parentItemId || null,
                });
                setName('');
                setParentItemId('');
              } finally {
                setBusy(false);
              }
            }}
          >
            作成
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <div>
          <Label>種別</Label>
          <select
            className="input"
            value={kind}
            onChange={(e) =>
              setKind(e.target.value as 'phase' | 'task' | 'milestone')
            }
          >
            <option value="phase">フェーズ</option>
            <option value="task">タスク</option>
            <option value="milestone">マイルストーン (1日)</option>
          </select>
        </div>
        <div>
          <Label>名称</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label>親行</Label>
          <select
            className="input"
            value={parentItemId}
            onChange={(e) => setParentItemId(e.target.value)}
          >
            <option value="">— なし (ルート) —</option>
            {items
              .slice()
              .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
              .map((i) => (
                <option key={i.itemId} value={i.itemId}>
                  {i.sortKey} {i.name}
                </option>
              ))}
          </select>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Tiny row inside the mobile burger menu. The desktop toolbar uses a
 * mix of icon + label and brand-coloured pill buttons; for the mobile
 * dropdown we collapse to a uniform list-style row so the menu reads
 * as one menu and not 5 inline buttons.
 */
function MobileMenuItem({
  onClick,
  icon,
  label,
  active,
  disabled,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 whitespace-nowrap rounded px-2 py-1.5 text-left text-xs',
        active
          ? 'bg-slate-900 text-white'
          : 'bg-white text-slate-700 hover:bg-slate-100',
        disabled ? 'cursor-not-allowed opacity-50 hover:bg-white' : '',
      )}
      role="menuitem"
    >
      {icon}
      <span className="flex-1">{label}</span>
    </button>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}
